import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  en: {
    translation: {
      nav: {
        dashboard: "Dashboard",
        calendar: "Calendar",
        journal: "Journal",
        horoscope: "Daily Horoscope",
        predictions: "Predictions",
        analytics: "Analytics",
        moonCycles: "Moon Cycles",
        wombHealing: "Womb Healing",
        remedies: "Remedies",
        astrology: "Astrology",
        transits: "Transits",
        synastry: "Partner Compatibility",
        rituals: "Rituals",
        numerology: "Numerology",
        aiTips: "AI Guidance",
        forum: "Community",
        achievements: "Achievements",
        settings: "Settings",
        admin: "Admin",
        signOut: "Sign Out",
      },
      dashboard: {
        welcome: "Welcome,",
        subtitle: "Here's your cosmic alignment for today",
        currentMoon: "Current Moon",
        yourCycle: "Your Cycle",
        moonEnergy: "Moon Energy",
        currentEnergy: "Current Energy",
        noData: "No Data",
        logPeriod: "Log your first period to see your cycle phase",
        logPeriodDate: "Please log your period start date",
        herbalRemedies: "Herbal Remedies",
        rituals: "Rituals",
        astrology: "Astrology",
        aiGuidance: "AI Guidance",
        todaysMeditation: "Today's Meditation",
        seeAll: "See all",
        nextNewMoon: "Next New Moon",
        nextFullMoon: "Next Full Moon",
        nextPeriod: "Next period in",
        days: "days",
        illuminated: "illuminated",
      },
      landing: {
        title: "Sacred Celestial Flow",
        subtitle: "Align with Your Natural Rhythms",
        description: "Track your menstrual cycle in harmony with the moon. Discover herbal remedies, sacred rituals, and personalized guidance for every phase of your journey.",
        beginJourney: "Begin Your Journey",
        signIn: "Sign In",
        embraceCycle: "Embrace Your Cycle",
      },
      auth: {
        welcomeBack: "Welcome Back",
        signInContinue: "Sign in to continue your journey",
        email: "Email",
        password: "Password",
        signIn: "Sign In",
        continueGoogle: "Continue with Google",
        noAccount: "Don't have an account?",
        createOne: "Create one",
        haveAccount: "Already have an account?",
        signInHere: "Sign in",
        createAccount: "Create Account",
        joinJourney: "Join the sacred journey",
        name: "Full Name",
        register: "Create Account",
      },
      settings: {
        title: "Settings",
        cycleSettings: "Cycle Settings",
        cycleLength: "Cycle Length",
        periodLength: "Period Length",
        save: "Save",
        subscription: "Subscription",
        notifications: "Notifications",
        weeklyDigest: "Weekly Cycle Digest",
        digestDesc: "Receive a weekly email with your moon forecast, cycle insights, and ritual recommendations.",
        enableDigest: "Enable Weekly Digest",
        deliveryDay: "Delivery Day",
        emailAddress: "Email Address",
        sendTestEmail: "Send Test Email",
        exportData: "Export Your Data",
        exportDesc: "Download your cycle logs, birth chart, and settings.",
        account: "Account",
        downloadReport: "Download PDF Report",
      },
      common: {
        loading: "Loading...",
        error: "Something went wrong",
        success: "Success",
        cancel: "Cancel",
        confirm: "Confirm",
        close: "Close",
        share: "Share",
        download: "Download",
        premium: "Premium",
        free: "Free",
        upgrade: "Upgrade",
      },
      partner: {
        inviteTitlePregnancy: "Invite your partner",
        inviteTitleTTC: "Share with your partner",
        inviteCopyPregnancy: "Let them follow each week's baby update, trimester milestones, and recent kick counts — no account needed.",
        inviteCopyTTC: "Let them see your fertile window and ovulation day so you're on the same page — no account needed.",
        createLink: "Create partner link",
        creating: "Creating...",
        share: "Share",
        revoke: "Revoke",
        copied: "Link copied",
        copyError: "Couldn't copy — long-press the link instead",
        linkReady: "Partner link ready — copy and send it",
        createError: "Couldn't create partner link",
        revokeError: "Couldn't revoke link",
        revokeConfirm: "Revoke this partner link? Anyone with it will lose access immediately.",
        revoked: "Link revoked",
        expires: "Expires",
        views: "view",
        views_plural: "views",
        shareTitlePregnancy: "Follow along with our baby's weekly updates",
        shareTitleTTC: "Follow along with our fertility window",
        viewHeader: "Partner view",
        journeyHeader: "{{name}}'s journey",
        readOnly: "A read-only window shared with love.",
        poweredBy: "Powered by Sacred Celestial Flow • {{name}} can revoke this link anytime.",
        linkUnavailable: "Link unavailable",
        babySize: "Baby is the size of a {{size}}",
        babyTiny: "tiny wonder",
        thisWeek: "This week",
        howToSupport: "How to support {{name}}",
        weekTrimester: "Week {{week}} • Trimester {{trimester}}",
        dueOn: "Due {{date}}",
        daysToGo: "{{days}} days to go",
        kicks24h: "{{count}} kicks in last 24h",
        fertileStatus: "Fertility Status",
        inWindow: "In the fertile window",
        notInWindow: "Not in fertile window",
        tonightMessage: "Tonight is a great night to be close with {{name}}.",
        ovulationIn: "Ovulation is in {{days}} day",
        ovulationIn_plural: "Ovulation is in {{days}} days",
        waitNextCycle: "Wait for the next cycle to begin.",
        fertileWindow: "Fertile window",
        ovulation: "Ovulation",
        cycleLength: "Cycle length",
        cycleDays: "{{count}} days",
        noCycleData: "Cycle data not yet available.",
        nothingToShow: "Nothing to show yet.",
        standardMessage: "Your partner isn't sharing detailed cycle info right now.",
      },
      whatsNew: {
        title: "What's new",
        subtitle: "Four fresh features crafted for your journey",
        gotIt: "Got it, let's explore",
        later: "Remind me later",
        aiInsightsTitle: "AI Cycle Insights",
        aiInsightsDesc: "Get warm, personalized guidance each day based on your cycle patterns — powered by Claude.",
        pregnancyTitle: "Pregnancy mode",
        pregnancyDesc: "Weekly baby updates, kick counter, and baby name generator — switch on in Settings.",
        ttcTitle: "Trying to conceive",
        ttcDesc: "BBT tracker, fertile window focus, and evidence-based conception tips.",
        partnerTitle: "Partner share links",
        partnerDesc: "Send a beautiful read-only link so your partner can follow your pregnancy or fertility window.",
        premiumTitle: "Unlock all 4 with Premium",
        premiumDesc: "Partner share, unlimited AI insights, fertility focus tools, and ad-free — one cozy plan.",
        premiumCta: "Upgrade to Premium",
      },
    },
  },
  es: {
    translation: {
      nav: {
        dashboard: "Panel",
        calendar: "Calendario",
        journal: "Diario",
        horoscope: "Horoscopo Diario",
        predictions: "Predicciones",
        analytics: "Analisis",
        moonCycles: "Ciclos Lunares",
        wombHealing: "Sanacion del Utero",
        remedies: "Remedios",
        astrology: "Astrologia",
        transits: "Transitos",
        synastry: "Compatibilidad",
        rituals: "Rituales",
        numerology: "Numerologia",
        aiTips: "Guia IA",
        forum: "Comunidad",
        achievements: "Logros",
        settings: "Configuracion",
        admin: "Administracion",
        signOut: "Cerrar Sesion",
      },
      dashboard: {
        welcome: "Bienvenida,",
        subtitle: "Tu alineacion cosmica para hoy",
        currentMoon: "Luna Actual",
        yourCycle: "Tu Ciclo",
        moonEnergy: "Energia Lunar",
        currentEnergy: "Energia Actual",
        noData: "Sin Datos",
        logPeriod: "Registra tu primer periodo para ver tu fase del ciclo",
        logPeriodDate: "Por favor registra la fecha de inicio de tu periodo",
        herbalRemedies: "Remedios Herbales",
        rituals: "Rituales",
        astrology: "Astrologia",
        aiGuidance: "Guia IA",
        todaysMeditation: "Meditacion de Hoy",
        seeAll: "Ver todo",
        nextNewMoon: "Proxima Luna Nueva",
        nextFullMoon: "Proxima Luna Llena",
        nextPeriod: "Proximo periodo en",
        days: "dias",
        illuminated: "iluminada",
      },
      landing: {
        title: "Sacred Celestial Flow",
        subtitle: "Alineate con Tus Ritmos Naturales",
        description: "Rastrea tu ciclo menstrual en armonia con la luna. Descubre remedios herbales, rituales sagrados y guia personalizada para cada fase de tu viaje.",
        beginJourney: "Comienza Tu Viaje",
        signIn: "Iniciar Sesion",
        embraceCycle: "Abraza Tu Ciclo",
      },
      auth: {
        welcomeBack: "Bienvenida de Nuevo",
        signInContinue: "Inicia sesion para continuar tu viaje",
        email: "Correo Electronico",
        password: "Contrasena",
        signIn: "Iniciar Sesion",
        continueGoogle: "Continuar con Google",
        noAccount: "No tienes una cuenta?",
        createOne: "Crear una",
        haveAccount: "Ya tienes una cuenta?",
        signInHere: "Inicia sesion",
        createAccount: "Crear Cuenta",
        joinJourney: "Unete al viaje sagrado",
        name: "Nombre Completo",
        register: "Crear Cuenta",
      },
      settings: {
        title: "Configuracion",
        cycleSettings: "Configuracion del Ciclo",
        cycleLength: "Duracion del Ciclo",
        periodLength: "Duracion del Periodo",
        save: "Guardar",
        subscription: "Suscripcion",
        notifications: "Notificaciones",
        weeklyDigest: "Resumen Semanal del Ciclo",
        digestDesc: "Recibe un correo semanal con tu pronostico lunar, informacion del ciclo y recomendaciones de rituales.",
        enableDigest: "Activar Resumen Semanal",
        deliveryDay: "Dia de Envio",
        emailAddress: "Correo Electronico",
        sendTestEmail: "Enviar Correo de Prueba",
        exportData: "Exportar Tus Datos",
        exportDesc: "Descarga tus registros de ciclo, carta natal y configuracion.",
        account: "Cuenta",
        downloadReport: "Descargar Informe PDF",
      },
      common: {
        loading: "Cargando...",
        error: "Algo salio mal",
        success: "Exito",
        cancel: "Cancelar",
        confirm: "Confirmar",
        close: "Cerrar",
        share: "Compartir",
        download: "Descargar",
        premium: "Premium",
        free: "Gratis",
        upgrade: "Mejorar",
      },
      partner: {
        inviteTitlePregnancy: "Invita a tu pareja",
        inviteTitleTTC: "Comparte con tu pareja",
        inviteCopyPregnancy: "Permite que siga cada actualizacion semanal del bebe, los hitos del trimestre y los conteos recientes de pataditas — sin crear cuenta.",
        inviteCopyTTC: "Permite que vea tu ventana fertil y dia de ovulacion para estar en sintonia — sin crear cuenta.",
        createLink: "Crear enlace de pareja",
        creating: "Creando...",
        share: "Compartir",
        revoke: "Revocar",
        copied: "Enlace copiado",
        copyError: "No se pudo copiar — manten presionado el enlace",
        linkReady: "Enlace listo — copialo y enviaselo",
        createError: "No se pudo crear el enlace",
        revokeError: "No se pudo revocar el enlace",
        revokeConfirm: "Revocar este enlace? Cualquiera que lo tenga perdera acceso inmediatamente.",
        revoked: "Enlace revocado",
        expires: "Expira",
        views: "vista",
        views_plural: "vistas",
        shareTitlePregnancy: "Acompana las actualizaciones semanales de nuestro bebe",
        shareTitleTTC: "Acompana nuestra ventana de fertilidad",
        viewHeader: "Vista de pareja",
        journeyHeader: "El viaje de {{name}}",
        readOnly: "Una ventana de solo lectura compartida con amor.",
        poweredBy: "Con tecnologia de Sacred Celestial Flow • {{name}} puede revocar este enlace en cualquier momento.",
        linkUnavailable: "Enlace no disponible",
        babySize: "El bebe es del tamano de un(a) {{size}}",
        babyTiny: "pequeno milagro",
        thisWeek: "Esta semana",
        howToSupport: "Como apoyar a {{name}}",
        weekTrimester: "Semana {{week}} • Trimestre {{trimester}}",
        dueOn: "Fecha prevista {{date}}",
        daysToGo: "Faltan {{days}} dias",
        kicks24h: "{{count}} pataditas en las ultimas 24h",
        fertileStatus: "Estado de Fertilidad",
        inWindow: "En la ventana fertil",
        notInWindow: "Fuera de la ventana fertil",
        tonightMessage: "Esta noche es ideal para estar cerca de {{name}}.",
        ovulationIn: "La ovulacion es en {{days}} dia",
        ovulationIn_plural: "La ovulacion es en {{days}} dias",
        waitNextCycle: "Espera a que comience el proximo ciclo.",
        fertileWindow: "Ventana fertil",
        ovulation: "Ovulacion",
        cycleLength: "Duracion del ciclo",
        cycleDays: "{{count}} dias",
        noCycleData: "Datos del ciclo aun no disponibles.",
        nothingToShow: "Aun no hay nada para mostrar.",
        standardMessage: "Tu pareja no esta compartiendo informacion detallada del ciclo ahora.",
      },
      whatsNew: {
        title: "Lo nuevo",
        subtitle: "Cuatro funciones frescas creadas para tu viaje",
        gotIt: "Entendido, a explorar",
        later: "Recuerdame luego",
        aiInsightsTitle: "IA del Ciclo",
        aiInsightsDesc: "Recibe orientacion calida y personalizada cada dia segun tus patrones de ciclo — con tecnologia de Claude.",
        pregnancyTitle: "Modo embarazo",
        pregnancyDesc: "Actualizaciones semanales del bebe, contador de pataditas y generador de nombres — actualo en Configuracion.",
        ttcTitle: "Buscando embarazo",
        ttcDesc: "Registro de TBC, enfoque en la ventana fertil y consejos basados en evidencia.",
        partnerTitle: "Enlaces para pareja",
        partnerDesc: "Envia un hermoso enlace de solo lectura para que tu pareja siga tu embarazo o ventana fertil.",
        premiumTitle: "Desbloquea los 4 con Premium",
        premiumDesc: "Enlaces para pareja, analisis IA ilimitados, herramientas de fertilidad y sin anuncios — un solo plan acogedor.",
        premiumCta: "Mejorar a Premium",
      },
    },
  },
  fr: {
    translation: {
      nav: {
        dashboard: "Tableau de bord",
        calendar: "Calendrier",
        journal: "Journal",
        horoscope: "Horoscope du jour",
        predictions: "Predictions",
        analytics: "Analyses",
        moonCycles: "Cycles lunaires",
        wombHealing: "Guerison uterine",
        remedies: "Remedes",
        astrology: "Astrologie",
        transits: "Transits",
        synastry: "Compatibilite",
        rituals: "Rituels",
        numerology: "Numerologie",
        aiTips: "Guide IA",
        forum: "Communaute",
        achievements: "Accomplissements",
        settings: "Parametres",
        admin: "Administration",
        signOut: "Deconnexion",
      },
      dashboard: {
        welcome: "Bienvenue,",
        subtitle: "Votre alignement cosmique du jour",
        currentMoon: "Lune actuelle",
        yourCycle: "Votre cycle",
        moonEnergy: "Energie lunaire",
        currentEnergy: "Energie actuelle",
        noData: "Aucune donnee",
        logPeriod: "Enregistrez vos premieres regles pour voir votre phase",
        logPeriodDate: "Veuillez enregistrer la date de debut de vos regles",
        herbalRemedies: "Remedes a base de plantes",
        rituals: "Rituels",
        astrology: "Astrologie",
        aiGuidance: "Guide IA",
        todaysMeditation: "Meditation du jour",
        seeAll: "Voir tout",
        nextNewMoon: "Prochaine nouvelle lune",
        nextFullMoon: "Prochaine pleine lune",
        nextPeriod: "Prochaines regles dans",
        days: "jours",
        illuminated: "illuminee",
      },
      landing: {
        title: "Sacred Celestial Flow",
        subtitle: "Alignez-vous avec vos rythmes naturels",
        description: "Suivez votre cycle menstruel en harmonie avec la lune. Decouvrez des remedes a base de plantes, des rituels sacres et des conseils personnalises.",
        beginJourney: "Commencer votre voyage",
        signIn: "Se connecter",
        embraceCycle: "Embrassez votre cycle",
      },
      auth: {
        welcomeBack: "Bon retour",
        signInContinue: "Connectez-vous pour continuer",
        email: "Email",
        password: "Mot de passe",
        signIn: "Se connecter",
        continueGoogle: "Continuer avec Google",
        continueApple: "Continuer avec Apple",
        noAccount: "Pas de compte ?",
        createOne: "Creer un compte",
        haveAccount: "Deja un compte ?",
        signInHere: "Se connecter",
        createAccount: "Creer un compte",
        joinJourney: "Rejoignez le voyage sacre",
        name: "Nom complet",
        register: "Creer un compte",
      },
      settings: {
        title: "Parametres",
        cycleSettings: "Parametres du cycle",
        cycleLength: "Duree du cycle",
        periodLength: "Duree des regles",
        save: "Enregistrer",
        subscription: "Abonnement",
        notifications: "Notifications",
        weeklyDigest: "Resume hebdomadaire",
        digestDesc: "Recevez un email hebdomadaire avec vos previsions lunaires et recommandations.",
        enableDigest: "Activer le resume hebdomadaire",
        deliveryDay: "Jour d'envoi",
        emailAddress: "Adresse email",
        sendTestEmail: "Envoyer un email test",
        exportData: "Exporter vos donnees",
        exportDesc: "Telechargez vos journaux de cycle, carte natale et parametres.",
        account: "Compte",
        downloadReport: "Telecharger le rapport PDF",
      },
      common: {
        loading: "Chargement...",
        error: "Une erreur est survenue",
        success: "Succes",
        cancel: "Annuler",
        confirm: "Confirmer",
        close: "Fermer",
        share: "Partager",
        download: "Telecharger",
        premium: "Premium",
        free: "Gratuit",
        upgrade: "Ameliorer",
      },
      partner: {
        inviteTitlePregnancy: "Invitez votre partenaire",
        inviteTitleTTC: "Partagez avec votre partenaire",
        inviteCopyPregnancy: "Laissez-le suivre chaque mise a jour hebdomadaire du bebe, les etapes du trimestre et les mouvements recents — sans compte.",
        inviteCopyTTC: "Laissez-le voir votre fenetre fertile et votre jour d'ovulation pour rester synchronises — sans compte.",
        createLink: "Creer un lien partenaire",
        creating: "Creation...",
        share: "Partager",
        revoke: "Revoquer",
        copied: "Lien copie",
        copyError: "Copie impossible — appuyez longuement sur le lien",
        linkReady: "Lien pret — copiez et envoyez-le",
        createError: "Impossible de creer le lien",
        revokeError: "Impossible de revoquer le lien",
        revokeConfirm: "Revoquer ce lien ? Toute personne qui le possede perdra l'acces immediatement.",
        revoked: "Lien revoque",
        expires: "Expire",
        views: "vue",
        views_plural: "vues",
        shareTitlePregnancy: "Suivez les mises a jour hebdomadaires de notre bebe",
        shareTitleTTC: "Suivez notre fenetre de fertilite",
        viewHeader: "Vue partenaire",
        journeyHeader: "Le voyage de {{name}}",
        readOnly: "Une fenetre en lecture seule partagee avec amour.",
        poweredBy: "Propulse par Sacred Celestial Flow • {{name}} peut revoquer ce lien a tout moment.",
        linkUnavailable: "Lien indisponible",
        babySize: "Le bebe a la taille d'un(e) {{size}}",
        babyTiny: "petite merveille",
        thisWeek: "Cette semaine",
        howToSupport: "Comment soutenir {{name}}",
        weekTrimester: "Semaine {{week}} • Trimestre {{trimester}}",
        dueOn: "Prevu le {{date}}",
        daysToGo: "Plus que {{days}} jours",
        kicks24h: "{{count}} coups de pied en 24h",
        fertileStatus: "Statut de fertilite",
        inWindow: "Dans la fenetre fertile",
        notInWindow: "Hors fenetre fertile",
        tonightMessage: "Ce soir est un bon moment pour etre proche de {{name}}.",
        ovulationIn: "Ovulation dans {{days}} jour",
        ovulationIn_plural: "Ovulation dans {{days}} jours",
        waitNextCycle: "Attendez le debut du prochain cycle.",
        fertileWindow: "Fenetre fertile",
        ovulation: "Ovulation",
        cycleLength: "Duree du cycle",
        cycleDays: "{{count}} jours",
        noCycleData: "Donnees du cycle pas encore disponibles.",
        nothingToShow: "Rien a afficher pour l'instant.",
        standardMessage: "Votre partenaire ne partage pas d'informations detaillees sur son cycle pour le moment.",
      },
      whatsNew: {
        title: "Nouveautes",
        subtitle: "Quatre nouvelles fonctionnalites concues pour votre voyage",
        gotIt: "Compris, explorons",
        later: "Me rappeler plus tard",
        aiInsightsTitle: "Analyses IA du cycle",
        aiInsightsDesc: "Recevez des conseils chaleureux et personnalises chaque jour selon vos schemas de cycle — propulse par Claude.",
        pregnancyTitle: "Mode grossesse",
        pregnancyDesc: "Mises a jour hebdomadaires du bebe, compteur de coups de pied, generateur de prenoms — activez dans Parametres.",
        ttcTitle: "Essais bebe",
        ttcDesc: "Suivi de temperature basale, focus fenetre fertile et conseils bases sur la science.",
        partnerTitle: "Liens partenaire",
        partnerDesc: "Envoyez un joli lien en lecture seule pour que votre partenaire suive votre grossesse ou fenetre de fertilite.",
        premiumTitle: "Debloquez les 4 avec Premium",
        premiumDesc: "Liens partenaire, analyses IA illimitees, outils de fertilite et sans pub — un seul abonnement chaleureux.",
        premiumCta: "Passer a Premium",
      },
    },
  },
  pt: {
    translation: {
      nav: {
        dashboard: "Painel",
        calendar: "Calendario",
        journal: "Diario",
        horoscope: "Horoscopo Diario",
        predictions: "Previsoes",
        analytics: "Analises",
        moonCycles: "Ciclos Lunares",
        wombHealing: "Cura Uterina",
        remedies: "Remedios",
        astrology: "Astrologia",
        transits: "Transitos",
        synastry: "Compatibilidade",
        rituals: "Rituais",
        numerology: "Numerologia",
        aiTips: "Guia IA",
        forum: "Comunidade",
        achievements: "Conquistas",
        settings: "Configuracoes",
        admin: "Administracao",
        signOut: "Sair",
      },
      dashboard: {
        welcome: "Bem-vinda,",
        subtitle: "Seu alinhamento cosmico de hoje",
        currentMoon: "Lua atual",
        yourCycle: "Seu ciclo",
        moonEnergy: "Energia lunar",
        currentEnergy: "Energia atual",
        noData: "Sem dados",
        logPeriod: "Registre sua primeira menstruacao para ver sua fase",
        logPeriodDate: "Por favor registre a data de inicio da sua menstruacao",
        herbalRemedies: "Remedios herbais",
        rituals: "Rituais",
        astrology: "Astrologia",
        aiGuidance: "Guia IA",
        todaysMeditation: "Meditacao de hoje",
        seeAll: "Ver tudo",
        nextNewMoon: "Proxima lua nova",
        nextFullMoon: "Proxima lua cheia",
        nextPeriod: "Proxima menstruacao em",
        days: "dias",
        illuminated: "iluminada",
      },
      landing: {
        title: "Sacred Celestial Flow",
        subtitle: "Alinhe-se com seus ritmos naturais",
        description: "Acompanhe seu ciclo menstrual em harmonia com a lua. Descubra remedios herbais, rituais sagrados e orientacao personalizada.",
        beginJourney: "Comece sua jornada",
        signIn: "Entrar",
        embraceCycle: "Abrace seu ciclo",
      },
      auth: {
        welcomeBack: "Bem-vinda de volta",
        signInContinue: "Entre para continuar sua jornada",
        email: "Email",
        password: "Senha",
        signIn: "Entrar",
        continueGoogle: "Continuar com Google",
        continueApple: "Continuar com Apple",
        noAccount: "Nao tem uma conta?",
        createOne: "Criar uma",
        haveAccount: "Ja tem uma conta?",
        signInHere: "Entrar",
        createAccount: "Criar conta",
        joinJourney: "Junte-se a jornada sagrada",
        name: "Nome completo",
        register: "Criar conta",
      },
      settings: {
        title: "Configuracoes",
        cycleSettings: "Configuracoes do ciclo",
        cycleLength: "Duracao do ciclo",
        periodLength: "Duracao da menstruacao",
        save: "Salvar",
        subscription: "Assinatura",
        notifications: "Notificacoes",
        weeklyDigest: "Resumo semanal do ciclo",
        digestDesc: "Receba um email semanal com previsoes lunares e recomendacoes de rituais.",
        enableDigest: "Ativar resumo semanal",
        deliveryDay: "Dia de envio",
        emailAddress: "Endereco de email",
        sendTestEmail: "Enviar email de teste",
        exportData: "Exportar seus dados",
        exportDesc: "Baixe seus registros de ciclo, mapa natal e configuracoes.",
        account: "Conta",
        downloadReport: "Baixar relatorio PDF",
      },
      common: {
        loading: "Carregando...",
        error: "Algo deu errado",
        success: "Sucesso",
        cancel: "Cancelar",
        confirm: "Confirmar",
        close: "Fechar",
        share: "Compartilhar",
        download: "Baixar",
        premium: "Premium",
        free: "Gratis",
        upgrade: "Melhorar",
      },
      partner: {
        inviteTitlePregnancy: "Convide seu parceiro",
        inviteTitleTTC: "Compartilhe com seu parceiro",
        inviteCopyPregnancy: "Deixe que ele acompanhe cada atualizacao semanal do bebe, marcos do trimestre e contagens recentes de chutes — sem precisar de conta.",
        inviteCopyTTC: "Deixe que ele veja sua janela fertil e dia de ovulacao para estarem sincronizados — sem precisar de conta.",
        createLink: "Criar link de parceiro",
        creating: "Criando...",
        share: "Compartilhar",
        revoke: "Revogar",
        copied: "Link copiado",
        copyError: "Nao foi possivel copiar — mantenha pressionado o link",
        linkReady: "Link pronto — copie e envie",
        createError: "Nao foi possivel criar o link",
        revokeError: "Nao foi possivel revogar o link",
        revokeConfirm: "Revogar este link? Qualquer um que o tenha perdera o acesso imediatamente.",
        revoked: "Link revogado",
        expires: "Expira",
        views: "visualizacao",
        views_plural: "visualizacoes",
        shareTitlePregnancy: "Acompanhe as atualizacoes semanais do nosso bebe",
        shareTitleTTC: "Acompanhe nossa janela de fertilidade",
        viewHeader: "Visao do parceiro",
        journeyHeader: "A jornada de {{name}}",
        readOnly: "Uma visao somente leitura compartilhada com amor.",
        poweredBy: "Desenvolvido por Sacred Celestial Flow • {{name}} pode revogar este link a qualquer momento.",
        linkUnavailable: "Link indisponivel",
        babySize: "O bebe e do tamanho de um(a) {{size}}",
        babyTiny: "pequeno milagre",
        thisWeek: "Esta semana",
        howToSupport: "Como apoiar {{name}}",
        weekTrimester: "Semana {{week}} • Trimestre {{trimester}}",
        dueOn: "Previsao {{date}}",
        daysToGo: "Faltam {{days}} dias",
        kicks24h: "{{count}} chutes nas ultimas 24h",
        fertileStatus: "Status de fertilidade",
        inWindow: "Na janela fertil",
        notInWindow: "Fora da janela fertil",
        tonightMessage: "Hoje e uma otima noite para estar perto de {{name}}.",
        ovulationIn: "Ovulacao em {{days}} dia",
        ovulationIn_plural: "Ovulacao em {{days}} dias",
        waitNextCycle: "Aguarde o inicio do proximo ciclo.",
        fertileWindow: "Janela fertil",
        ovulation: "Ovulacao",
        cycleLength: "Duracao do ciclo",
        cycleDays: "{{count}} dias",
        noCycleData: "Dados do ciclo ainda indisponiveis.",
        nothingToShow: "Nada para mostrar ainda.",
        standardMessage: "Sua parceira nao esta compartilhando informacoes detalhadas do ciclo agora.",
      },
      whatsNew: {
        title: "Novidades",
        subtitle: "Quatro recursos novos criados para sua jornada",
        gotIt: "Entendi, vamos explorar",
        later: "Lembrar depois",
        aiInsightsTitle: "Insights IA do ciclo",
        aiInsightsDesc: "Receba orientacoes quentes e personalizadas diariamente com base nos padroes do seu ciclo — com tecnologia Claude.",
        pregnancyTitle: "Modo gravidez",
        pregnancyDesc: "Atualizacoes semanais do bebe, contador de chutes e gerador de nomes — ative em Configuracoes.",
        ttcTitle: "Tentando engravidar",
        ttcDesc: "Registro de TBC, foco na janela fertil e dicas baseadas em ciencia.",
        partnerTitle: "Links para parceiro",
        partnerDesc: "Envie um lindo link somente leitura para seu parceiro acompanhar sua gravidez ou janela de fertilidade.",
        premiumTitle: "Desbloqueie os 4 com Premium",
        premiumDesc: "Link para parceiro, insights IA ilimitados, ferramentas de fertilidade e sem anuncios — um plano aconchegante.",
        premiumCta: "Atualizar para Premium",
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
  });

export default i18n;
